import React, { useRef, useEffect } from 'react';
import { noop, toNumber } from 'lodash';
import { v4 as uuid } from 'uuid';
import { globalEventBus } from '@fiverr-private/futile';
import {
    IMPRESSION_ITEM_ATTR,
    CONTAINER_ID_ATTR,
    IMPRESSION_PARENT_ATTR,
    OVERFLOWED_ITEM_ATTR
} from '../useImpressionContainer/consts';
import {
    getItemRegisteredInManagerEvent,
    REGISTER_IMPRESSION_ITEM_EVENT,
    SEND_IMPRESSION_EVENT
} from '../useImpressionManager/consts';
import {
    getContainersData,
    getElementImpId,
    isElementImpressed,
    isElementOverflowsParent,
    markElementAsImpressed
} from '../../utils';

/**
 *
 * @param {Object} [options={}]
 * @param {Object} [options.selfRef=null] - React ref, used when container is also an item.
 * @param {string} [options.selfId=null] - Generated id, used when container is also an item.
 * @param {Object} [options.enrichment={}] - An object that will be added to each impression event.
 * @param {boolean} [options.sendImpressionToBQ] - Should send impression event to BigQuery.
 * @param {func} [options.onImpression] - Callback that will be called when impression event is sent.
 * @param {number} [options.intersectionThreshold] - Indicate at what percentage of the target's visibility the observer's callback should be executed.
 * @returns {Array} Array that contains ref to the impression item.
 */
const useImpressionItem = ({
    selfRef = null,
    selfId = null,
    enrichment = {},
    sendImpressionToBQ = true,
    onImpression = noop,
    intersectionThreshold = 1
} = {}) => {

    const itemRef = useRef(null);
    const ref = selfRef || itemRef;
    const impressionIdRef = useRef(getElementImpId(ref.current) || selfId || uuid());
    const itemId = impressionIdRef.current;
    const itemEnrichment = { imp_id: itemId, ...enrichment };

    const sendImpressionEvent = ([intersectionObserverEntry]) => {
        const { target, intersectionRect } = intersectionObserverEntry;
        let { isIntersecting } = intersectionObserverEntry;

        const element = ref.current || target;

        if (isElementImpressed(element)) {
            return;
        }

        const { containerId, containerRef, nestingLevel, isLeaf, isOverflowed } = getEventData(element);

        if (!isIntersecting && toNumber(intersectionThreshold) === 0) {
            isIntersecting = true;
        }

        if (isIntersecting) {
            const itemOffset = {
                top: Math.round(intersectionRect.top - document.body.getBoundingClientRect().top)
            };

            markElementAsImpressed(element);
            observer.current.unobserve(element);

            globalEventBus.emit(SEND_IMPRESSION_EVENT,
                {
                    intersectionObserverEntry,
                    containerId,
                    containerRef,
                    itemId,
                    itemEnrichment,
                    nestingLevel,
                    isLeaf,
                    isOverflowed,
                    itemOffset,
                    sendImpressionToBQ,
                    onImpression
                });
        }
    };

    const isIntersectionObserverExists = typeof IntersectionObserver !== 'undefined' && window?.IntersectionObserver;

    const observer = useRef(isIntersectionObserverExists && new IntersectionObserver(sendImpressionEvent, { threshold: intersectionThreshold }));

    useEffect(() => {
        if (!ref.current || isElementImpressed(ref.current)) {
            return;
        }

        const { current } = ref;

        const startObservingItem = () => {
            observer.current?.observe(current);
        };

        const stopObservingItem = () => {
            observer.current?.unobserve(current);
        };

        globalEventBus.on(getItemRegisteredInManagerEvent(itemId), startObservingItem);

        return () => {
            globalEventBus.off(getItemRegisteredInManagerEvent(itemId), startObservingItem);
            stopObservingItem();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);

    const sendRegisterEvent = (containerId, selfIdAsContainer) => {
        globalEventBus.emit(REGISTER_IMPRESSION_ITEM_EVENT, { itemId, containerId, selfIdAsContainer });
    };

    const getEventData = (itemRef) => {
        if (!itemRef) {
            return {};
        }

        const { containerId, containerRef, containersList } = getContainersData(itemRef);
        const nestingLevel = containersList.length;
        const isLeaf = itemRef.getAttribute(CONTAINER_ID_ATTR) === null;
        const selfIdAsContainer = itemRef.getAttribute(CONTAINER_ID_ATTR);
        const isOverflowed = itemRef.getAttribute(OVERFLOWED_ITEM_ATTR) === 'true';

        return { containerId, containerRef, nestingLevel, isLeaf, selfIdAsContainer, isOverflowed };
    };

    useEffect(() => {
        if (!ref.current || isElementImpressed(ref.current)) {
            return;
        }

        const { current } = ref;

        const { containerId, containerRef, selfIdAsContainer } = getEventData(current);

        current.setAttribute(IMPRESSION_ITEM_ATTR, itemId);

        if (containerId) {
            current.setAttribute(IMPRESSION_PARENT_ATTR, containerId);

            if (isElementOverflowsParent(current, containerRef)) {
                current.setAttribute(OVERFLOWED_ITEM_ATTR, true);
            }
        }

        sendRegisterEvent(containerId, selfIdAsContainer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);

    return [ref, itemId];
};

export { useImpressionItem };
